import React from "react";
import { MolecularBg } from "../components/MolecularBg";
import { Beaker, Shield, FlaskConical, Microscope, FileCheck2, Truck } from "lucide-react";
import { useSEO } from "../lib/useSEO";

export const AboutPage = () => {
    useSEO({
        title: "About Us — Carbon Labs Premium Research Peptides Australia",
        description: "Carbon Labs provides Australian researchers premium research peptides backed by transparent quality standards, traceable sourcing, and reliable fulfilment.",
    });

    return (
        <div className="pt-24 pb-20 min-h-screen relative" data-testid="about-page">
            <MolecularBg intensity="medium" />
            <div className="container-x relative z-10">
                <span className="eyebrow">// About Carbon Labs</span>
                <h1 className="font-display italic text-4xl md:text-5xl font-black text-white tracking-tighter mt-1 mb-6 max-w-3xl uppercase">
                    Built for serious<br />Australian research.
                </h1>
                <div className="max-w-3xl space-y-5 mb-12">
                    <p className="text-base text-[var(--text-secondary)] leading-relaxed">
                        Carbon Labs was founded to provide researchers with access to premium research
                        compounds supported by transparent quality standards, traceable sourcing, and
                        reliable Australian fulfilment.
                    </p>
                    <p className="text-base text-[var(--text-secondary)] leading-relaxed">
                        We focus on product consistency, manufacturer-supplied documentation, and professional
                        customer support to help researchers obtain dependable materials for laboratory
                        and analytical applications.
                    </p>
                </div>

                <div className="mb-12">
                    <h2 className="font-display italic uppercase text-2xl font-black text-white mb-5">Our commitment</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {[
                            { i: FileCheck2, t: "Quality First", d: "Every batch held to the same exacting standard." },
                            { i: Microscope, t: "Manufacturer Documentation", d: "Manufacturer-supplied Certificate of Analysis available for every product." },
                            { i: Beaker, t: "Manufacturer-Supplied COAs", d: "A manufacturer-supplied Certificate of Analysis is available for every manufacturing batch." },
                            { i: Truck, t: "Fast Australian Dispatch", d: "Cold-chain dispatched from Sydney within 24 hours." },
                            { i: FlaskConical, t: "Research Use Focused", d: "Strictly research-grade. Not for human use." },
                            { i: Shield, t: "Professional Support", d: "Direct access to our lab team for technical questions." },
                        ].map((it, i) => (
                            <div key={i} className="glass-elevated rounded-2xl p-5">
                                <it.i size={22} className="text-[var(--gold)] mb-3" />
                                <h3 className="font-display italic uppercase font-bold text-white text-sm mb-1.5">{it.t}</h3>
                                <p className="text-xs text-[var(--text-secondary)] leading-relaxed">{it.d}</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="glass-elevated rounded-3xl p-8 md:p-10 neon-border-purple max-w-3xl">
                    <h2 className="font-display italic uppercase text-xl md:text-2xl font-black text-white mb-3">
                        Our purity standard
                    </h2>
                    <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-3">
                        We refuse to ship any peptide below 98% purity, with most batches exceeding 99%. If
                        a vial fails our internal QC, it never reaches inventory. This is the standard we'd
                        demand if we were buying for our own bench — and it's the one we hold ourselves to.
                    </p>
                    <p className="text-xs font-mono uppercase tracking-widest text-[var(--gold)]">
                        Manufacturer-supplied Certificate of Analysis available for every product.
                    </p>
                </div>
            </div>
        </div>
    );
};
